import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SendInterface.css';

export default function SendInterface({ apiBase }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channelMap, setChannelMap] = useState({});
  const [customChannel, setCustomChannel] = useState('');
  const [customChannels, setCustomChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sendErrors, setSendErrors] = useState([]);
  const [success, setSuccess] = useState('');
  const [showDryRun, setShowDryRun] = useState(false);
  const [dryRunData, setDryRunData] = useState(null);

  useEffect(() => {
    loadTemplates();
    loadChannels();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBase}/templates`, {
        withCredentials: true,
      });
      setTemplates(res.data.templates);
    } catch (err) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadChannels = async () => {
    try {
      const res = await axios.get(`${apiBase}/channels`, {
        withCredentials: true,
      });
      setChannels(res.data.channels);
      setChannelMap(res.data.channel_map || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load channels');
    }
  };

  const addCustomChannel = () => {
    const value = customChannel.trim();
    if (!value) return;
    const isUsername = /^@[\w]{5,32}$/.test(value);
    const isId = /^-?\d+$/.test(value);
    if (!isUsername && !isId) {
      setError('Channel must be @username or a numeric chat ID');
      return;
    }
    setError('');
    setCustomChannels((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setCustomChannel('');
  };

  const handleTemplateSelect = async (templateName) => {
    try {
      setSelectedTemplate(templateName);
      const res = await axios.get(`${apiBase}/templates/${encodeURIComponent(templateName)}`, {
        withCredentials: true,
      });
      setTemplateData(res.data);
    } catch (err) {
      setError('Failed to load template');
    }
  };

  const handleChannelToggle = (channel) => {
    setSelectedChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel]
    );
  };

  const handleDryRun = async () => {
    if (!selectedTemplate || selectedChannels.length === 0) {
      setError('Please select a template and at least one channel');
      return;
    }

    try {
      const res = await axios.post(
        `${apiBase}/send/dry-run`,
        {
          template_name: selectedTemplate,
          channels: selectedChannels,
        },
        { withCredentials: true }
      );
      setDryRunData(res.data.preview);
      setShowDryRun(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Dry run failed');
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate || selectedChannels.length === 0) {
      setError('Please select a template and at least one channel');
      return;
    }

    if (!window.confirm(`Send "${selectedTemplate}" to ${selectedChannels.length} channel(s)?`)) {
      return;
    }

    try {
      setSending(true);
      setError('');
      setSuccess('');
      setSendErrors([]);

      const res = await axios.post(
        `${apiBase}/send`,
        {
          template_name: selectedTemplate,
          channels: selectedChannels,
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        setSuccess(`✓ Sent to ${res.data.sent} channel(s)`);
      } else {
        setError(`Failed to send to ${res.data.failed} channel(s)`);
        setSendErrors(Array.isArray(res.data.errors) ? res.data.errors : []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="send-interface">
      <h2>✈ Send Messages</h2>

      {error && <div className="alert alert-error">❌ {error}</div>}
      {sendErrors.length > 0 && (
        <div className="alert alert-error">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Details</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {sendErrors.slice(0, 20).map((e, idx) => (
              <li key={idx}>
                {e.channel ? `${e.channel}: ` : ''}
                {e.type ? `[${e.type}] ` : ''}
                {e.error_code ? `(code ${e.error_code}) ` : ''}
                {e.method ? `${e.method}: ` : ''}
                {e.message || String(e)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="send-grid">
        {/* Left: Template Selector */}
        <div className="send-panel">
          <h3>Template</h3>
          {loading ? (
            <p>Loading templates...</p>
          ) : (
            <select
              className="form-control"
              value={selectedTemplate || ''}
              onChange={(e) => handleTemplateSelect(e.target.value)}
            >
              <option value="">Select a template...</option>
              {templates.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Center: Template Preview */}
        {templateData && (
          <div className="send-panel">
            <h3>Preview</h3>
            <div className="template-preview">
              <div className="preview-section">
                <label>Parse Mode:</label>
                <p>{templateData.parse_mode || '(none)'}</p>
              </div>

              {templateData.media && (
                <div className="preview-section">
                  <label>📎 Media:</label>
                  <p>
                    {templateData.media.type.toUpperCase()} -{' '}
                    {templateData.media.source}
                  </p>
                </div>
              )}

              <div className="preview-section">
                <label>Text:</label>
                <p className="preview-text">{templateData.text}</p>
              </div>

              {templateData.buttons && templateData.buttons.length > 0 && (
                <div className="preview-section">
                  <label>Buttons:</label>
                  <div className="buttons-grid">
                    {templateData.buttons.map((row, idx) => (
                      <div key={idx} className="button-row">
                        {row.map((btn, bidx) => (
                          <button key={bidx} className="preview-btn" disabled>
                            {btn.text}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right: Channels & Actions */}
        <div className="send-panel">
          <h3>Channels</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              className="form-control"
              value={customChannel}
              onChange={(e) => setCustomChannel(e.target.value)}
              placeholder="Add channel: @username or -100..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomChannel();
                }
              }}
            />
            <button
              className="btn btn-secondary"
              type="button"
              onClick={addCustomChannel}
              disabled={!customChannel.trim()}
            >
              Add
            </button>
          </div>
          <div className="channels-list">
            {channels.map((channel) => (
              <label key={channel} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedChannels.includes(channel)}
                  onChange={() => handleChannelToggle(channel)}
                />
                <span>
                  {channel}
                  {channelMap[channel] ? (
                    <span style={{ opacity: 0.75, marginLeft: 6 }}>
                      ({channelMap[channel]})
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
            {customChannels.map((channel) => (
              <label key={channel} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedChannels.includes(channel)}
                  onChange={() => handleChannelToggle(channel)}
                />
                <span>
                  {channel}
                  <span style={{ opacity: 0.75, marginLeft: 6 }}>(custom)</span>
                </span>
              </label>
            ))}
          </div>

          <div className="action-buttons">
            <button
              className="btn btn-success"
              onClick={handleDryRun}
              disabled={!selectedTemplate || selectedChannels.length === 0 || sending}
            >
              🔍 Dry Run
            </button>
            <button
              className="btn btn-success"
              onClick={handleSend}
              disabled={!selectedTemplate || selectedChannels.length === 0 || sending}
            >
              {sending ? 'Sending...' : '✈ Send'}
            </button>
          </div>
        </div>
      </div>

      {/* Dry Run Modal */}
      {showDryRun && (
        <div className="modal-overlay" onClick={() => setShowDryRun(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔍 Dry Run Preview</h3>
              <button
                className="modal-close"
                onClick={() => setShowDryRun(false)}
              >
                ✕
              </button>
            </div>

            {dryRunData && (
              <div className="dry-run-content">
                <div className="preview-section">
                  <strong>Template:</strong> {dryRunData.name}
                </div>
                <div className="preview-section">
                  <strong>Channels:</strong> {dryRunData.channels.join(', ')}
                </div>
                <div className="preview-section">
                  <strong>Parse Mode:</strong> {dryRunData.parse_mode}
                </div>

                {dryRunData.media && (
                  <div className="preview-section">
                    <strong>Media:</strong>{' '}
                    {dryRunData.media.type} - {dryRunData.media.source}
                  </div>
                )}

                <div className="preview-section">
                  <strong>Text:</strong>
                  <p className="preview-text">{dryRunData.text}</p>
                </div>

                {dryRunData.buttons && dryRunData.buttons.length > 0 && (
                  <div className="preview-section">
                    <strong>Buttons:</strong>
                    <div className="buttons-grid">
                      {dryRunData.buttons.map((row, idx) => (
                        <div key={idx} className="button-row">
                          {row.map((btn, bidx) => (
                            <button key={bidx} className="preview-btn" disabled>
                              {btn.text}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDryRun(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
